'use client';

import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { initializePaddle, type Paddle, type PaddleEventData } from '@paddle/paddle-js';

interface SubscriptionContextValue {
  paddle: Paddle | null;
  subscribePaddleEvent: (listener: PaddleEventListener) => () => void;
}

type PaddleEnvironment = 'sandbox' | 'production';
export type PaddleEventListener = (event: PaddleEventData) => void;

const DEFAULT_PADDLE_ENVIRONMENT: PaddleEnvironment = 'sandbox';

const SubscriptionContext = createContext<SubscriptionContextValue | null>(null);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const [paddle, setPaddle] = useState<Paddle | null>(null);
  const listenersRef = useRef<Set<PaddleEventListener>>(new Set());

  const subscribePaddleEvent = useCallback((listener: PaddleEventListener) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  useEffect(() => {
    const token = process.env.NEXT_PUBLIC_PADDLE_CLIENT_TOKEN;
    if (!token) {
      return;
    }

    let mounted = true;
    initializePaddle({
      environment: (process.env.NEXT_PUBLIC_PADDLE_ENVIRONMENT as PaddleEnvironment) ?? DEFAULT_PADDLE_ENVIRONMENT,
      token,
      eventCallback: (event) => {
        listenersRef.current.forEach((listener) => listener(event));
      },
    }).then((paddleInstance) => {
      if (mounted && paddleInstance) {
        setPaddle(paddleInstance);
      }
    });

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <SubscriptionContext.Provider value={{ paddle, subscribePaddleEvent }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscriptionContext(): SubscriptionContextValue {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error('SubscriptionProvider not mounted');
  return ctx;
}

export function usePaddle(): {
  paddle: Paddle | null;
  subscribe: (listener: PaddleEventListener) => () => void;
} {
  const { paddle, subscribePaddleEvent } = useSubscriptionContext();
  return { paddle, subscribe: subscribePaddleEvent };
}
